import _ from "lodash";
import ko from "knockout";
import Wrapup from "models/Wrapup";
import liveDataService from "services/liveDataService";
import ObservableModel from "util/ObservableModel";
import Contact from "models/outbound/Contact";
import Participant from "models/Participant";
import communicationMixin from "models/comunicationMixin";

//TODO: this is potentially masking some subtle issues with attribute
// overwriting on construction.
const getAttributeSafely = function (participant, attributeName) {
    if (
        _.has(participant, "attributes") &&
        _.has(participant.attributes(), attributeName)
    ) {
        return participant.attributes()[attributeName]();
    }
};

/**
 * @constructor
 * @this Call
 * Observable representation of a call. In addition to the
 * public API fields, exposes an observable field 'active'
 * that indicates whether or not the call is live. The call object that is returned
 * from the server is expected to have the following data:
 *
 * <pre><code>
 * {
 *     id: "1234a",
 *     selfuri: "http://inin.com/blah",
 *     participants: [
 *       {
 *            id: "1233",
 *            calls: [{
 *              muted: false,
 *              held: false,
 *              state: "CONNECTING|ALERTING|CONNECTED|DISCONNECTED"
 *            }],
 *            address: "1234567890",
 *            name: "Taco",
 *            userId: "passwordIsTaco",
 *            selfUri: "http://inin.com/foo"
 *       }
 *     ]
 * }
 * </code></pre>
 *
 * Participants on a call can have the following purpose values:
 *
 *   manual: The callee on a normal outbound call
 *   inbound:
 *   customer:
 *   dialer: This callee on a Dialer Preview or Power outbound call
 *   dialer.system: The system (e.g. Edge/Twilio) that placed the Dialer Power outbound call
 *   acd: The agent routing/matching engine for inbound/outbound calls
 *   ivr: The IVR/menu engine for inbound calls
 *
 * @return {Call} a call
 */
const Call = ObservableModel.extend(
    {
        defaults: {
            maxParticipants: null,
            participants: [],
            wrapup: null,
        },
        modelProperties: {
            wrapup: Wrapup,
            contact: Contact,
            participants: Participant,
        },
    },
    function () {
        const self = this;

        self.interactionType = ko.observable("call");

        communicationMixin(self); //Adds functionality shared between different communication types.

        // For Dialer calls, this will hold the contact information
        self.contact = ko.observable(null);

        self.errorInfo = ko.computed(function () {
            if (
                self.participantForCurrentUser() &&
                self.participantForCurrentUser().errorInformation()
            ) {
                return self.participantForCurrentUser().errorInformation();
            }
            return null;
        });

        self.displayedError = ko.observable();

        const isAcdParticipant = function (participant) {
            if (!participant.purpose) {
                return false;
            }

            return participant.purpose && participant.purpose() === "acd";
        };

        /**
         * Track connected HUMAN participants and customers (customers are tracked regardless of connected status)
         * @return {[type]} [description]
         */
        self.otherConnectedParticipants = ko.computed(function () {
            const connectedOrCustomer = _.reject(
                self.otherParticipants(),
                function (participant) {
                    // reject if participant is disconnected or a monitoring participant and they're not a customer
                    const currentParticipant = participant;
                    return (
                        (currentParticipant.isDisconnected() ||
                            // TODO:  the public-api will eventually filter these out.  This can be removed when that is done.
                            currentParticipant.monitoredParticipantId() !==
                                null ||
                            currentParticipant.coachedParticipantId() !==
                                null ||
                            currentParticipant.bargedParticipantId() !==
                                null) &&
                        !_.some(
                            self.customerParticipants(),
                            function (customer) {
                                return currentParticipant.id === customer.id;
                            }
                        )
                    );
                }
            );
            return connectedOrCustomer;
        });

        /**
         * Track HUMAN participants (except for monitoring participants) who participated in the call to show on ACW screen
         * Note that users with multiple participants will be deduplicated.
         * @return {[participant Object]} Array of participant objects
         */
        self.afterCallWorkSummaryParticipants = ko.computed(function () {
            // filter out if participant is a monitoring participant
            let allParticipants = _.filter(
                self.otherParticipants(),
                function (currentParticipant) {
                    return _.isEmpty(
                        currentParticipant.monitoredParticipantId()
                    );
                }
            );

            //let's remove duplicates if any - this can happen if transfers occur to same user
            allParticipants = _.uniqBy(allParticipants, function (participant) {
                return participant.userId();
            });
            return allParticipants;
        });

        /**
         * Returns the identified customer on the call
         *
         * TODO: For now this is the manner in which we are identifying the customer to be shown in ACW
         * We first identify the participant with purpose of 'customer' if not found then we identify
         * the first participant with purpose of 'user'... if nothing there then with purpose of 'agent'
         *
         */
        self.afterCallWorkCustomerParticipant = ko.computed(function () {
            //First identify participant with purpose of customer
            let customer = _.find(
                self.afterCallWorkSummaryParticipants(),
                function (participant) {
                    return (
                        participant.purpose &&
                        participant.purpose() === "customer"
                    );
                }
            );

            //Second if empty then try looking for first participant with purpose of user
            if (!customer) {
                customer = _.find(
                    self.afterCallWorkSummaryParticipants(),
                    function (participant) {
                        return (
                            participant.purpose &&
                            participant.purpose() === "user"
                        );
                    }
                );
            } else {
                return customer;
            }

            //Third if still empty then try looking for first participant with purpose of agent
            if (!customer) {
                customer = _.find(
                    self.afterCallWorkSummaryParticipants(),
                    function (participant) {
                        return (
                            participant.purpose &&
                            participant.purpose() === "agent"
                        );
                    }
                );
            } else {
                return customer;
            }

            //Fourth if nothing still then simply return null
            if (!customer) {
                return null;
            } else {
                return customer;
            }
        });

        /**
         * Retrieve all ACD participants.
         */
        self.acdParticipants = ko.computed(function () {
            return _.filter(self.participants(), isAcdParticipant);
        });

        /**
         * Retrieve ACD participant for the current user's queue.
         */
        self.acdParticipantForQueue = ko.computed(function () {
            if (
                !self.participantForCurrentUser() ||
                !self.participantForCurrentUser().queueId
            ) {
                return false;
            }

            const queueId = self.participantForCurrentUser().queueId();

            // Using a reverse find to ensure that if multiple acd participants
            // exist for this queue, that we use the later one to handle the
            // case where a call from transferred from Queue A to B then back to A.

            return _.findLast(self.acdParticipants(), function (participant) {
                return participant.queueId() === queueId;
            });
        });

        /**
         * Returns whether or not this call is a monitored call
         *
         * @return {Boolean} true if the call is monitored, otherwise false
         */
        self.isMonitor = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }
            return (
                self.participantForCurrentUser().monitoredParticipantId() !==
                null
            );
        });

        /**
         * Returns the dialer participant if it exists
         *
         * @return {Object} the dialer participant, null if it does not exist
         */
        self.dialerParticipant = ko.computed(function () {
            let dialerParticipant = null;

            _.forEach(self.participants(), function (participant) {
                if (participant.purpose && participant.purpose() === "dialer") {
                    dialerParticipant = participant;
                    return false; // exit the iteration now that we have what we want
                }
                return true;
            });

            return dialerParticipant;
        });

        /**
         * A helper method to determine if this is a user call
         *
         * @return {Boolean} true if it is a user call, otherwise false.
         */
        self.isUserCall = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                return self.participantForCurrentUser().isUser();
            }
            return false;
        });

        /**
         * A helper method to determine if this is an agent call
         *
         * @return {Boolean} true if it is a agent call, otherwise false.
         */
        self.isAgentCall = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                return self.participantForCurrentUser().isAgent();
            }
            return false;
        });

        /**
         * A helper method to determine if this call was placed by Dialer.
         *
         * @return {Boolean} [description] true if the call is a Dialer call, false otherwise.
         */
        self.isDialerCall = ko.computed(function () {
            // check for dialer participant or dialer interaction id on the agent
            return (
                _.isObject(self.dialerParticipant()) ||
                (_.has(self.participantForCurrentUser(), "attributes") &&
                    _.has(
                        self.participantForCurrentUser().attributes(),
                        "dialerInteractionId"
                    ))
            );
        });

        /**
         * Is current participant recording this conversation
         */
        self.isRecording = ko.computed(function () {
            if (
                !self.participantForCurrentUser() ||
                !self.participantForCurrentUser().currentCall()
            ) {
                return false;
            }

            return self.participantForCurrentUser().currentCall().recording();
        });

        /**
         * Is there at least one other Human participant recording this conversation
         */
        self.isOtherParticipantRecording = ko.computed(function () {
            let otherIsRecording = false;
            _.each(self.otherParticipants(), function (participant) {
                const call = participant.currentCall();
                if (call && call.recording()) {
                    otherIsRecording = true;
                    return false;
                }
            });

            return otherIsRecording;
        });

        /**
         * If the call is currently doing after call work, this will be true;
         */
        self.isInAfterCallWork = ko.observable(false);

        self.isEligibleForAfterCallWork = ko.computed(function () {
            return (
                self.participantForCurrentUser() &&
                self.participantForCurrentUser().wrapupRequired()
            );
        });

        /**
         * A helper method to determine if the user's call is in an active status (i.e. a
         * a status where the user has not disconnected from the call).
         *
         * @return {Boolean} true if the call is in a connected status, false otherwise.
         */
        self.active = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            // call is active if status is in a connected state or the call is in
            // ACW and doesn't already have any wrapup saved for the current user
            return (
                !self.isDisconnected() ||
                (self.participantForCurrentUser().wrapupRequired() &&
                    !self.participantForCurrentUser().wrapup())
            );
        });

        /**
         * if wrapupPrompt = 'forcedTimeout' then this observable will be true. This means that the
         * agent will face a timer counting up and will remain in ACW until counter reaches the amount of time
         * defined in wrapupTimeoutMs
         */
        self.isACWForcedTimeout = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                const wrapupPrompt = ko.unwrap(
                    self.participantForCurrentUser().wrapupPrompt
                );
                const wrapupTimeoutMs = ko.unwrap(
                    self.participantForCurrentUser().wrapupTimeoutMs
                );
                return (
                    !_.isEmpty(wrapupTimeoutMs) &&
                    !_.isEmpty(wrapupPrompt) &&
                    wrapupPrompt.toLowerCase() === "forcedtimeout"
                );
            }
            return false;
        });

        /**
         * if wrapupPrompt ='timeout' then this observable will be true. This means that the agent will face a counter
         * counting down and they must submit the ACW before the timer expires.
         */
        self.isACWTimeout = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                const wrapupPrompt = ko.unwrap(
                    self.participantForCurrentUser().wrapupPrompt
                );
                const wrapupTimeoutMs = ko.unwrap(
                    self.participantForCurrentUser().wrapupTimeoutMs
                );
                return (
                    !_.isEmpty(wrapupTimeoutMs) &&
                    !_.isEmpty(wrapupPrompt) &&
                    wrapupPrompt.toLowerCase() === "timeout"
                );
            }
            return false;
        });

        /**
         * Is true if ACW is either timeout or forced timeout.
         * @type {*}
         */
        self.isNonDiscretionaryCompletion = ko.computed(function () {
            return self.isACWForcedTimeout() || self.isACWTimeout();
        });

        /**
         * If this is true, the user will not be able to opt out of aftercallwork
         *
         */
        self.afterCallWorkMandatory = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                const wrapupPrompt = ko.unwrap(
                    self.participantForCurrentUser().wrapupPrompt
                );
                return (
                    !_.isEmpty(wrapupPrompt) &&
                    (wrapupPrompt.toLowerCase() === "mandatory" ||
                        self.isNonDiscretionaryCompletion())
                );
            }
            return false;
        });

        /**
         * hasMissedTimer will be true if timer for forced timeout or timeout has expired
         * with the agent not completing ACW. The backend will add a system defined code
         * in this case and the hasMissedTimer will be true.
         */
        self.hasMissedTimer = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                const wrapup = ko.unwrap(
                    self.participantForCurrentUser().wrapup()
                );

                if (
                    self.isNonDiscretionaryCompletion() &&
                    wrapup &&
                    wrapup.code
                ) {
                    const wrapupCode = ko.unwrap(wrapup.code());
                    if (wrapupCode && wrapupCode === "ININ-WRAP-UP-TIMEOUT") {
                        return true;
                    }
                }
            }
            return false;
        });

        self.consultInitiatorParticipant = ko.computed(function () {
            return _.find(self.participants(), function (participant) {
                return (
                    participant.consultParticipantId() &&
                    !participant.isDisconnected() &&
                    getAttributeSafely(participant, "consultInitiator")
                );
            });
        });

        self.consultParticipant = ko.computed(function () {
            return _.find(self.participants(), function (participant) {
                return (
                    participant.consultParticipantId() &&
                    participant.consultParticipantId() === participant.id
                );
            });
        });

        self.consultDestinationParticipant = ko.computed(function () {
            return _.find(self.participants(), function (participant) {
                return (
                    participant.consultParticipantId() &&
                    !participant.isDisconnected() &&
                    participant.consultParticipantId() !== participant.id &&
                    !getAttributeSafely(participant, "consultInitiator")
                );
            });
        });

        /**
         * Provides access to the appropriate consult transfer participant for the current user.
         * If current user is the initiator, then show the destination
         * otherwise show the initiator
         */
        self.consultDisplayParticipant = ko.computed(function () {
            if (
                self.consultInitiatorParticipant() &&
                self.consultInitiatorParticipant().id ===
                    self.participantForCurrentUser().id
            ) {
                return self.consultDestinationParticipant();
            }

            return self.consultInitiatorParticipant();
        });

        /** Define the call as a consult transfer if the current user is participating
         * in the consult, and if the other two participants have been found. If we don't
         * have all of them, we can't operate on the consult correctly.
         */
        self.isConsultTransfer = ko.computed(function () {
            return (
                self.participantForCurrentUser() &&
                self.participantForCurrentUser().consultParticipantId() &&
                self.consultInitiatorParticipant() &&
                self.consultParticipant() &&
                !self.consultParticipant().isDisconnected() &&
                self.consultDestinationParticipant()
            );
        });

        self.isConference = ko.computed(function () {
            return !_.isNull(self.maxParticipants());
        });

        /**
         * Returns whether or not this call is a coached call
         *
         * @return {Boolean} true if the call is coached, otherwise false
         */
        self.isCoach = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }
            return (
                self.participantForCurrentUser().coachedParticipantId() !== null
            );
        });

        /**
         * Returns whether or not this call is a bargedIn call
         *
         * @return {Boolean} true if the call is bargedIn, otherwise false
         */
        self.isBargeIn = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }
            return (
                self.participantForCurrentUser().bargedParticipantId() !== null
            );
        });
    }
);

liveDataService.registerTopic("calls", Call);

export default Call;
